import { useCallback, useEffect, useRef, useState } from "react";
import type { WebSocketTranscriptionResponse } from "@acto/taskpane/core/features/transcription/types";

interface UseTranscribeOptions {
  onTranscript?: (sessionText: string) => void;
  /**
   * How loud the microphone is right now, from 0 (silence) to 1 (the loudest the format can
   * represent), throttled to ~15 updates/s. Opt-in: the loudness pass is skipped when this is
   * absent, so callers that only need the transcript pay nothing.
   */
  onLoudness?: (loudness: number) => void;
  tenant?: string;
  user?: string;
  language?: string;
}

// ~15 updates/s. The worklet posts one message per 128 audio frames (~125/s at SAMPLE_RATE), far
// more than a loudness meter shows or React should re-render for.
const LOUDNESS_INTERVAL_MS = 66;

/**
 * Root-mean-square loudness of one chunk of 16-bit audio. Squaring is what makes it work on a
 * waveform: the samples swing symmetrically around zero, so a plain average of them is near zero
 * however loud the audio is.
 */
const computeLoudness = (buffer: ArrayBuffer): number => {
  const samples = new Int16Array(buffer);
  if (samples.length === 0) return 0;

  let sumOfSquares = 0;
  for (const raw of samples) {
    // A 16-bit sample runs to +/-32768, so this puts it on a -1 to 1 scale.
    const sample = raw / 0x8000;
    sumOfSquares += sample * sample;
  }

  return Math.sqrt(sumOfSquares / samples.length);
};

/** Never yields a leading, trailing, or doubled separator, whichever segments are empty. */
const joinTranscript = (...segments: string[]): string => segments.filter(Boolean).join(" ");

interface UseTranscribeReturn {
  isRecording: boolean;
  /**
   * Resolves false when no session is recording: permission refused or dismissed, no input device,
   * no AudioWorklet, or a stop or a later start that landed while this one was still opening. No
   * `end` follows.
   */
  startRecording: () => Promise<boolean>;
  stopRecording: () => Promise<void>;
}

const generateSessionId = () => {
  // Use crypto.randomUUID() for guaranteed uniqueness
  return crypto.randomUUID();
};

/**
 * Checks if the browser supports AudioWorklet API for real-time audio processing.
 * This is required for the transcription feature to work.
 *
 * @returns Promise that resolves to true if AudioWorklet is supported, false otherwise
 */
export const checkAudioWorkletSupport = async (): Promise<boolean> => {
  try {
    const context = new AudioContext();
    const supported = !!context.audioWorklet;
    await context.close();
    return supported;
  } catch {
    return false;
  }
};

export const RECONNECT_BASE_DELAY_MS = 500;
const RECONNECT_MAX_DELAY_MS = 10000;
const SAMPLE_RATE = 16000;
const BYTES_PER_SAMPLE = 2;
const MAX_BUFFERED_DURATION_MS = 8000;
const MAX_BUFFERED_BYTES = Math.floor((SAMPLE_RATE * BYTES_PER_SAMPLE * MAX_BUFFERED_DURATION_MS) / 1000);

export const useTranscribe = ({
  onTranscript,
  onLoudness,
  tenant,
  user,
  language,
}: UseTranscribeOptions = {}): UseTranscribeReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const lastLoudnessAtRef = useRef(0);

  const streamRef = useRef<MediaStream | null>(null);
  const websocketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const bufferedChunksRef = useRef<ArrayBuffer[]>([]);
  const bufferedBytesRef = useRef(0);
  const isConnectionReadyRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  /**
   * Bumped by every start and every teardown. Handlers capture the generation they were installed
   * in and go inert once it passes, so a teardown still awaiting the server cannot act on the
   * session that replaced it.
   */
  const generationRef = useRef(0);
  /**
   * Bumped by every start only. A socket being wound down still delivers the engine's parting
   * message, which is part of this recording; a transcript from a recording the user has already
   * replaced is not.
   */
  const recordingRef = useRef(0);
  /**
   * The server accumulates the transcript per connection and starts a replaced one from empty, so
   * text from a connection this hook has dropped is never sent again. Prepending it is what keeps
   * `onTranscript` monotonic across a reconnect.
   */
  const carriedTranscriptRef = useRef("");
  const liveTranscriptRef = useRef("");

  const cleanupResources = useCallback(async (options: { sendEndSignal: boolean } = { sendEndSignal: false }) => {
    // Every teardown closes the generation, not only those routed through `stopRecording`: a
    // socket that closes during the awaits below would otherwise reach a live `onclose` and
    // reconnect into a torn-down session.
    generationRef.current += 1;
    isConnectionReadyRef.current = false;
    bufferedChunksRef.current = [];
    bufferedBytesRef.current = 0;

    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (workletNodeRef.current) {
      workletNodeRef.current.port.postMessage({ type: "stop" });
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    // A start that publishes to these refs while `close()` is pending must not have its own
    // context nulled and its own microphone stopped by this teardown.
    const audioContext = audioContextRef.current;
    const stream = streamRef.current;
    audioContextRef.current = null;
    streamRef.current = null;

    if (audioContext) await audioContext.close();
    if (stream) stream.getTracks().forEach((track) => track.stop());

    if (websocketRef.current) {
      const ws = websocketRef.current;
      websocketRef.current = null;

      if (options.sendEndSignal && ws.readyState === WebSocket.OPEN) {
        try {
          console.log("[WS] Sending end signal and waiting for server response...");
          ws.send(
            JSON.stringify({
              message: { type: "end" },
            }),
          );

          // Wait for server to close the connection or timeout after 3s
          const closedPromise = new Promise<void>((resolve) => {
            const timeoutId = window.setTimeout(() => {
              console.log("[WS] Timeout waiting for server close, closing anyway");
              resolve();
            }, 3000);

            ws.addEventListener(
              "close",
              () => {
                console.log("[WS] Server closed connection gracefully");
                window.clearTimeout(timeoutId);
                resolve();
              },
              { once: true },
            );
          });

          await closedPromise;
        } catch (error) {
          console.error("[WS] Failed to send end signal:", error);
        }
      }

      // Detached only once the socket is done talking — see `recordingRef`.
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;

      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING ||
        ws.readyState === WebSocket.CLOSING
      ) {
        ws.close();
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      void cleanupResources({ sendEndSignal: false });
    };
  }, [cleanupResources]);

  const stopRecording = useCallback(async () => {
    setIsRecording(false);
    await cleanupResources({ sendEndSignal: true });
  }, [cleanupResources]);

  const startRecording = useCallback(async (): Promise<boolean> => {
    const generation = (generationRef.current += 1);
    const recording = (recordingRef.current += 1);
    const ownsHook = () => generation === generationRef.current;
    const sameRecording = () => recording === recordingRef.current;

    try {
      bufferedChunksRef.current = [];
      bufferedBytesRef.current = 0;
      isConnectionReadyRef.current = false;
      reconnectAttemptsRef.current = 0;
      carriedTranscriptRef.current = "";
      liveTranscriptRef.current = "";

      if (reconnectTimeoutRef.current !== null) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      const sessionId = generateSessionId();
      const wsBaseUrl = import.meta.env["VITE_INTELLIGENCE_LAYER_WS_URL"] || "wss://api.heyacto.com/intelligence_layer";

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      // Nothing reaches the shared refs until past the last await below, so releasing what this
      // invocation holds is all a superseded start has to do.
      if (!ownsHook()) {
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }

      const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });

      const releaseUnpublished = async () => {
        stream.getTracks().forEach((track) => track.stop());
        await audioContext.close();
      };

      if (!audioContext.audioWorklet) {
        await releaseUnpublished();
        console.warn("[Transcribe] AudioWorklet not supported in this browser");
        return false;
      }

      const workletModuleUrl = new URL("./transcribe-audio-worklet-processor.js?url", import.meta.url).href;
      await audioContext.audioWorklet.addModule(workletModuleUrl);

      if (!ownsHook()) {
        await releaseUnpublished();
        return false;
      }

      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, "transcribe-audio-worklet-processor", {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });

      streamRef.current = stream;
      audioContextRef.current = audioContext;
      sourceRef.current = source;
      workletNodeRef.current = workletNode;

      function flushBufferedChunks() {
        const ws = websocketRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN || !isConnectionReadyRef.current) {
          return;
        }
        const queue = bufferedChunksRef.current;
        while (queue.length > 0) {
          const chunk = queue[0];

          try {
            ws.send(chunk);
            queue.shift();
            bufferedBytesRef.current -= chunk.byteLength;
          } catch (sendError) {
            console.error("[WS] Failed to send buffered audio chunk:", sendError);
            break;
          }
        }
      }

      function enqueueChunk(chunk: ArrayBuffer) {
        bufferedChunksRef.current.push(chunk);
        bufferedBytesRef.current += chunk.byteLength;

        while (bufferedBytesRef.current > MAX_BUFFERED_BYTES && bufferedChunksRef.current.length > 0) {
          const dropped = bufferedChunksRef.current.shift();
          if (dropped) {
            bufferedBytesRef.current -= dropped.byteLength;
          }
        }

        flushBufferedChunks();
      }

      function clearReconnectTimeout() {
        if (reconnectTimeoutRef.current !== null) {
          window.clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      }

      const scheduleReconnect = () => {
        if (!ownsHook()) {
          return;
        }

        carriedTranscriptRef.current = joinTranscript(carriedTranscriptRef.current, liveTranscriptRef.current);
        liveTranscriptRef.current = "";

        clearReconnectTimeout();
        const attempt = reconnectAttemptsRef.current;
        const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
        reconnectAttemptsRef.current = attempt + 1;
        reconnectTimeoutRef.current = window.setTimeout(() => {
          connectWebSocket();
        }, delay);
      };

      const connectWebSocket = () => {
        if (!ownsHook()) {
          return;
        }

        const params = new URLSearchParams({ session_id: sessionId });
        if (tenant) params.set("tenant", tenant);
        if (user) params.set("user", user);
        if (language) params.set("language", language);
        const wsUrl = `${wsBaseUrl}/meeting_recap/transcribe?${params.toString()}`;
        const ws = new WebSocket(wsUrl);
        ws.binaryType = "arraybuffer";
        websocketRef.current = ws;
        isConnectionReadyRef.current = false;

        ws.onopen = () => {
          if (!ownsHook()) return;
          reconnectAttemptsRef.current = 0;
        };

        ws.onmessage = (event) => {
          if (!sameRecording()) return;

          try {
            const response: WebSocketTranscriptionResponse = JSON.parse(event.data);
            const message = response.message;

            if (!message) return;

            if (message.type === "connection_ack") {
              if (!ownsHook()) return;

              isConnectionReadyRef.current = true;
              setIsRecording(true);
              flushBufferedChunks();
            } else if (message.type === "error") {
              console.error("[Transcription] Backend error:", {
                code: message.error_code,
                message: message.error_message,
              });
              void stopRecording();
              return;
            } else if (message.type === "text") {
              liveTranscriptRef.current = message.text;

              if (onTranscript) {
                onTranscript(joinTranscript(carriedTranscriptRef.current, message.text));
              }
            } else if (message.type === "end") {
              console.log("[Transcription] Server ended stream (silence detected)");
              setIsRecording(false);
              void cleanupResources({ sendEndSignal: false });
              return;
            }
          } catch (err) {
            console.error("[WS] Error parsing message:", err);
          }
        };

        ws.onerror = (event) => {
          if (!ownsHook()) return;

          const error = new Error(`WebSocket error: ${event}`);
          console.error("[WS] Error:", error);
        };

        ws.onclose = () => {
          if (!ownsHook()) return;

          console.debug("[WS] Connection closed");
          isConnectionReadyRef.current = false;
          scheduleReconnect();
        };
      };

      workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        if (!ownsHook()) return;

        if (onLoudness) {
          const now = performance.now();
          if (now - lastLoudnessAtRef.current >= LOUDNESS_INTERVAL_MS) {
            lastLoudnessAtRef.current = now;
            onLoudness(computeLoudness(event.data));
          }
        }

        enqueueChunk(event.data);
      };

      workletNode.port.postMessage({ type: "start" });

      source.connect(workletNode);
      workletNode.connect(audioContext.destination);

      connectWebSocket();
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error("[Transcribe] Failed to start recording:", error);
      // The microphone may already be open by here.
      await cleanupResources();
      return false;
    }
  }, [onTranscript, onLoudness, stopRecording, cleanupResources, tenant, user, language]);

  return {
    isRecording,
    startRecording,
    stopRecording,
  };
};
